/**
 * Bedrock 모델별 토큰 단가 (USD per 1M tokens)
 * 2026년 기준 - 크로스 리전 inference (Global CRIS)
 * 
 * 참고: https://aws.amazon.com/bedrock/pricing/
 * 마지막 업데이트: 2026-10
 */

interface ModelPrice {
  input: number;  // USD per 1M input tokens
  output: number; // USD per 1M output tokens
}

const MODEL_PRICING: Record<string, ModelPrice> = {
  // ══════════════════════════════════════════════════════════════════════
  // Amazon Nova
  // ══════════════════════════════════════════════════════════════════════
  'nova-micro':     { input: 0.035,  output: 0.14   },
  'nova-lite':      { input: 0.06,   output: 0.24   },
  'nova-pro':       { input: 0.80,   output: 3.20   },
  'nova-premier':   { input: 2.50,   output: 12.50  },
  'nova-2-lite':    { input: 0.06,   output: 0.24   },

  // ══════════════════════════════════════════════════════════════════════
  // Anthropic Claude - Haiku (빠른/저렴)
  // ══════════════════════════════════════════════════════════════════════
  'claude-haiku-4-5':  { input: 1.00,   output: 5.00   },
  'claude-haiku-5-5':  { input: 0.80,   output: 4.00   },

  // ══════════════════════════════════════════════════════════════════════
  // Anthropic Claude - Sonnet (밸런스)
  // ══════════════════════════════════════════════════════════════════════
  'claude-sonnet-4':     { input: 3.00,   output: 15.00  },
  'claude-sonnet-4-5':   { input: 3.00,   output: 15.00  },
  'claude-sonnet-4-6':   { input: 3.00,   output: 15.00  },
  'claude-sonnet-5':     { input: 2.00,   output: 10.00  },
  'claude-sonnet-5-5':   { input: 2.00,   output: 10.00  },

  // ══════════════════════════════════════════════════════════════════════
  // Anthropic Claude - Opus (프리미엄)
  // ══════════════════════════════════════════════════════════════════════
  'claude-opus-4-5':   { input: 5.00,   output: 25.00  },
  'claude-opus-4-6':   { input: 5.00,   output: 25.00  },
  'claude-opus-4-7':   { input: 5.00,   output: 25.00  },
  'claude-opus-4-8':   { input: 5.00,   output: 25.00  },
  'claude-opus-5':     { input: 5.00,   output: 25.00  },
  'claude-opus-5-5':   { input: 4.00,   output: 20.00  },

  // ══════════════════════════════════════════════════════════════════════
  // Anthropic Claude - Fable (최고급)
  // ══════════════════════════════════════════════════════════════════════
  'claude-fable-5':    { input: 10.00,  output: 50.00  },
  'claude-fable-5-1':  { input: 10.00,  output: 50.00  },

  // ══════════════════════════════════════════════════════════════════════
  // OpenAI GPT (Bedrock)
  // ══════════════════════════════════════════════════════════════════════
  'gpt-5-4':           { input: 2.00,   output: 10.00  },
  'gpt-5-5':           { input: 2.00,   output: 10.00  },
  'gpt-5-6-luna':      { input: 0.10,   output: 0.50   },
  'gpt-5-6-sol':       { input: 2.00,   output: 10.00  },
  'gpt-5-6-terra':     { input: 2.00,   output: 10.00  },
  'gpt-6-luna':        { input: 0.10,   output: 0.50   },
  'gpt-6-sol':         { input: 2.00,   output: 10.00  },
  'gpt-6-astra':       { input: 10.00,  output: 50.00  },
  'gpt-6-1-sol':       { input: 2.00,   output: 10.00  },
};

/**
 * Bedrock 모델 ID에서 단가 키 추출
 */
function extractPriceKey(modelId: string): string {
  // 1. 리전 접두사 제거 (us., eu., apac., global.)
  let key = modelId.replace(/^(us|eu|apac|global)\./, '');
  
  // 2. 제공자 접두사 제거 (anthropic., amazon., openai.)
  key = key.replace(/^(anthropic|amazon|openai)\./, '');
  
  // 3. 버전 suffix 제거 (-vN:0, -YYYYMMDD-vN:0, :0)
  key = key.replace(/-v\d+:\d+$/, '');
  key = key.replace(/-\d{8}-v\d+:\d+$/, '');
  key = key.replace(/:\d+$/, '');
  
  // 4. 모델명 정규화 매핑
  const normalizations: Array<[RegExp, string]> = [
    // Amazon Nova
    [/^nova-micro/, 'nova-micro'],
    [/^nova-lite/, 'nova-lite'],
    [/^nova-pro/, 'nova-pro'],
    [/^nova-premier/, 'nova-premier'],
    [/^amazon-nova-2-lite/, 'nova-2-lite'],
    [/^nova-2-lite/, 'nova-2-lite'],
    
    // Claude Haiku
    [/^claude-haiku-4-5/, 'claude-haiku-4-5'],
    [/^claude-haiku-5-5/, 'claude-haiku-5-5'],
    
    // Claude Sonnet
    [/^claude-sonnet-4($|-20)/, 'claude-sonnet-4'],
    [/^claude-sonnet-4-5/, 'claude-sonnet-4-5'],
    [/^claude-sonnet-4-6/, 'claude-sonnet-4-6'],
    [/^claude-sonnet-5($|-20)/, 'claude-sonnet-5'],
    [/^claude-sonnet-5-5/, 'claude-sonnet-5-5'],
    
    // Claude Opus
    [/^claude-opus-4-5/, 'claude-opus-4-5'],
    [/^claude-opus-4-6/, 'claude-opus-4-6'],
    [/^claude-opus-4-7/, 'claude-opus-4-7'],
    [/^claude-opus-4-8/, 'claude-opus-4-8'],
    [/^claude-opus-5($|-20)/, 'claude-opus-5'],
    [/^claude-opus-5-5/, 'claude-opus-5-5'],
    
    // Claude Fable
    [/^claude-fable-5($|-20)/, 'claude-fable-5'],
    [/^claude-fable-5-1/, 'claude-fable-5-1'],
    
    // OpenAI GPT
    [/^gpt-5\.4/, 'gpt-5-4'],
    [/^gpt-5\.5/, 'gpt-5-5'],
    [/^gpt-5\.6-luna/, 'gpt-5-6-luna'],
    [/^gpt-5\.6-sol/, 'gpt-5-6-sol'],
    [/^gpt-5\.6-terra/, 'gpt-5-6-terra'],
    [/^gpt-6-luna/, 'gpt-6-luna'],
    [/^gpt-6-sol/, 'gpt-6-sol'],
    [/^gpt-6-astra/, 'gpt-6-astra'],
    [/^gpt-6\.1-sol/, 'gpt-6-1-sol'],
  ];
  
  for (const [pattern, priceKey] of normalizations) {
    if (pattern.test(key)) {
      return priceKey;
    }
  }
  
  return key;
}

const KRW_PER_USD = 1400;

/**
 * 모델 ID와 토큰 수로 비용 계산
 */
export function calcCostFront(
  modelId: string,
  inputTokens: number,
  outputTokens: number
): { costUsd: number; costKrw: number } {
  const key = extractPriceKey(modelId);
  const price = MODEL_PRICING[key];
  
  if (!price) {
    // 가격 정보 없으면 기본값 (Sonnet 4.6 기준)
    console.warn(`[Pricing] 알 수 없는 모델: ${modelId} (key: ${key}), 기본 가격 적용`);
    const defaultPrice = MODEL_PRICING['claude-sonnet-4-6']!;
    const costUsd = (inputTokens * defaultPrice.input + outputTokens * defaultPrice.output) / 1_000_000;
    const costKrw = Math.round(costUsd * KRW_PER_USD);
    return { costUsd, costKrw };
  }

  const costUsd = (inputTokens * price.input + outputTokens * price.output) / 1_000_000;
  const costKrw = Math.round(costUsd * KRW_PER_USD);

  return { costUsd, costKrw };
}

/**
 * 모델 ID로 단가 정보 조회
 */
export function getModelPricing(modelId: string): ModelPrice | null {
  const key = extractPriceKey(modelId);
  return MODEL_PRICING[key] || null;
}
